// @/components/themes/ui/cart-provider.tsx
"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { SK } from "@/constants";
import { handleSetCartInfoStorageItems } from "@/utilities/cartManager";

export interface Product {
  imageurl: string;
  id: number;
  name: string;
  sp: number;
  mrp: number;
  gst_rate: string;
  size_id: string | null;
  colorcodes_id: number | null;
  descp: string;
  shrtdesc: string;
  category_name: string;
  selectedSize?: { code: string };
  selectedWeight?: { code: string };
  "Custom Text"?: string;
  "Custom Text1"?: string;
  "Men Size"?: string;
  "Couple Size"?: string;
}

export interface CartItem extends Product {
  cartQuantity: number;
  selectedSize?: { code: string };
  selectedColor?: { code: string };
}

interface CartContextType {
  cartItems: CartItem[];
  itemCount: number;
  addToCart: (
    product: Product,
    quantity: number,
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => Promise<void>;
  removeFromCart: (
    productId: number,
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => Promise<void>;
  updateQuantity: (
    productId: number,
    newQuantity: number,
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => Promise<void>;
  clearCart: () => void;
  setCartItems: (items: CartItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [itemCount, setItemCount] = useState(0);

  useEffect(() => {
    const initializeCart = async () => {
      if (typeof window === "undefined") return;

      const storedCart = localStorage.getItem(SK.CARTINFODATA);
      if (storedCart) {
        try {
          const parsedCart = JSON.parse(storedCart);
          if (Array.isArray(parsedCart.products)) {
            const initializedItems = parsedCart.products.map(
              (item: CartItem) => ({
                ...item,
                cartQuantity: item.cartQuantity || 1, // Ensure default quantity
              })
            );
            setCartItems(initializedItems);
            setItemCount(
              initializedItems.reduce(
                (sum: number, item: CartItem) => sum + (item.cartQuantity || 1),
                0
              )
            );
          }
        } catch (error) {
          console.error("Error parsing cart from localStorage:", error);
        }
      }

      const userData = localStorage.getItem(SK.NEXTUSERINFODATA);
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user?.id) {
            const response = await fetch("/api/USERCARTINFOFETCHAPI", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId: user.id }),
            });
            if (response.ok) {
              const data = await response.json();
              const fetchedCartItems = (data.cartItems || []).map(
                (item: CartItem) => ({
                  ...item,
                  cartQuantity: item.cartQuantity || 1, // Ensure default quantity
                })
              );
              setCartItems(fetchedCartItems);
              updateLocalStorage(fetchedCartItems);
            }
          }
        } catch (error) {
          console.error("Error fetching cart from API:", error);
        }
      }
    };

    initializeCart();
  }, []);

  const updateLocalStorage = (items: CartItem[]) => {
    const subtotal = items.reduce(
      (sum, item) => sum + Number(item.sp) * (item.cartQuantity || 1),
      0
    );
    const cartInfo = {
      cartcount: items.length,
      cartquantitycount: items.reduce(
        (sum, item) => sum + (item.cartQuantity || 1),
        0
      ),
      products: items,
      cartprice: subtotal,
      subtotal: subtotal.toFixed(2),
      mrptotal: items
        .reduce(
          (sum, item) => sum + Number(item.mrp) * (item.cartQuantity || 1),
          0
        )
        .toFixed(2),
      shipping: subtotal >= 299 ? "0.00" : "45.00",
      ordertotal: (subtotal + (subtotal >= 299 ? 0 : 45)).toFixed(2),
      total: subtotal.toFixed(2),
    };
    if (typeof window !== "undefined") {
      localStorage.setItem(SK.CARTINFODATA, JSON.stringify(cartInfo));
      handleSetCartInfoStorageItems(cartInfo);
    }
    setItemCount(cartInfo.cartquantitycount);
  };

  const addToCart = async (
    product: Product,
    quantity: number = 1, // Default to 1
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => {
    try {
      const existingItemIndex = cartItems.findIndex(
        (item) =>
          item.id === product.id &&
          item.selectedSize?.code === selectedSize?.code &&
          item.selectedColor?.code === selectedColor?.code
      );
      let updatedItems = [...cartItems];
      if (existingItemIndex > -1) {
        updatedItems[existingItemIndex].cartQuantity = Math.min(
          10,
          (updatedItems[existingItemIndex].cartQuantity || 1) + quantity
        );
      } else {
        updatedItems = [
          ...cartItems,
          { ...product, cartQuantity: quantity, selectedSize, selectedColor },
        ];
      }
      setCartItems(updatedItems);
      updateLocalStorage(updatedItems);

      const userData = localStorage.getItem(SK.NEXTUSERINFODATA);
      if (userData) {
        const user = JSON.parse(userData);
        if (user?.id) {
          await fetch("/api/USERCARTINFOFETCHPUSHAPI", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              cartItems: updatedItems,
            }),
          });
        }
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const removeFromCart = async (
    productId: number,
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => {
    try {
      const updatedItems = cartItems.filter(
        (item) =>
          !(
            item.id === productId &&
            item.selectedSize?.code === selectedSize?.code &&
            item.selectedColor?.code === selectedColor?.code
          )
      );
      setCartItems(updatedItems);
      updateLocalStorage(updatedItems);

      const userData = localStorage.getItem(SK.NEXTUSERINFODATA);
      if (userData) {
        const user = JSON.parse(userData);
        if (user?.id) {
          await fetch("/api/USERCARTINFOFETCHCLEARAPI", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              productId,
              selectedSize,
              selectedColor,
            }),
          });
        }
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const updateQuantity = async (
    productId: number,
    newQuantity: number,
    selectedSize?: { code: string },
    selectedColor?: { code: string }
  ) => {
    try {
      const updatedItems = cartItems.map((item) =>
        item.id === productId &&
          item.selectedSize?.code === selectedSize?.code &&
          item.selectedColor?.code === selectedColor?.code
          ? { ...item, cartQuantity: Math.max(1, Math.min(10, newQuantity)) }
          : item
      );
      setCartItems(updatedItems);
      updateLocalStorage(updatedItems);

      const userData = localStorage.getItem(SK.NEXTUSERINFODATA);
      if (userData) {
        const user = JSON.parse(userData);
        if (user?.id) {
          await fetch("/api/USERCARTINFOFETCHPUSHAPI", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              cartItems: updatedItems,
            }),
          });
        }
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const clearCart = () => {
    setCartItems([]);
    updateLocalStorage([]);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        itemCount,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        setCartItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    // console.warn("useCart must be used within a CartProvider");
    return {
      cartItems: [],
      itemCount: 0,
      addToCart: async () => { },
      removeFromCart: async () => { },
      updateQuantity: async () => { },
      clearCart: () => { },
      setCartItems: () => { },
    };
  }
  return context;
};
